import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ChevronLeft, ChevronRight, Copy, ExternalLink, Image as ImageIcon, Loader2, Monitor, Trash2, Video } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const ROLES = ["staff", "branch_admin", "admin", "super_admin"];

async function uploadToCloudinary(file, kind, onProgress) {
  const { data: sig } = await api.get("/admin/display/upload-signature", { params: { resource_type: kind } });
  const form = new FormData();
  form.append("file", file);
  form.append("api_key", sig.api_key);
  form.append("timestamp", sig.timestamp);
  form.append("signature", sig.signature);
  form.append("folder", sig.folder);
  const res = await new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `https://api.cloudinary.com/v1_1/${sig.cloud_name}/${kind}/upload`);
    xhr.upload.onprogress = (e) => e.lengthComputable && onProgress(Math.round((e.loaded / e.total) * 100));
    xhr.onload = () => (xhr.status < 300 ? resolve(JSON.parse(xhr.responseText)) : reject(new Error(JSON.parse(xhr.responseText)?.error?.message || "Upload failed")));
    xhr.onerror = () => reject(new Error("Upload failed"));
    xhr.send(form);
  });
  return { url: res.secure_url, public_id: res.public_id, resource_type: kind, duration: res.duration || 0 };
}

export default function StaffDisplay() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [cfg, setCfg] = useState(null);
  const [text, setText] = useState("");
  const [seconds, setSeconds] = useState(10);
  const [imageSeconds, setImageSeconds] = useState(8);
  const [thankSeconds, setThankSeconds] = useState(8);
  const [pin, setPin] = useState("");
  const dragId = useRef(null);

  const reorder = async (ids) => {
    setCfg((c) => ({ ...c, media: ids.map((id) => c.media.find((m) => m.id === id)) }));
    try { await api.put("/admin/display/media/order", { ids }); } catch { toast.error("Could not save order"); }
  };
  const move = (from, to) => {
    const ids = (cfg.media || []).map((m) => m.id);
    if (to < 0 || to >= ids.length || from === to) return;
    const [id] = ids.splice(from, 1); ids.splice(to, 0, id);
    reorder(ids);
  };
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => {
    if (loading) return;
    if (!user || !ROLES.includes(user.role)) { navigate("/login"); return; }
    api.get("/admin/display").then(({ data }) => { setCfg(data); setText(data.text || ""); setSeconds(data.customer_seconds || 10); setImageSeconds(data.image_seconds || 8); setThankSeconds(data.thank_you_seconds || 8); setPin(data.unlock_pin || ""); }).catch(() => toast.error("Could not load display settings"));
  }, [user, loading, navigate]);

  const save = async (patch) => {
    setSaving(true);
    try { const { data } = await api.put("/admin/display", patch); setCfg(data); toast.success("Saved"); }
    catch { toast.error("Could not save"); }
    finally { setSaving(false); }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const kind = file.type.startsWith("video/") ? "video" : file.type.startsWith("image/") ? "image" : null;
    if (!kind) { toast.error("Only images or videos"); return; }
    if (file.size > 100 * 1024 * 1024) { toast.error("File must be under 100MB"); return; }
    setProgress(0);
    try {
      const media = await uploadToCloudinary(file, kind, setProgress);
      const { data } = await api.post("/admin/display/media", media);
      setCfg((c) => ({ ...c, media: [...(c.media || []), data] }));
      toast.success(`${kind === "video" ? "Video" : "Image"} added`);
    } catch (err) { toast.error(err.message || "Upload failed"); }
    finally { setProgress(null); }
  };

  const remove = async (id) => {
    try { await api.delete(`/admin/display/media/${id}`); setCfg((c) => ({ ...c, media: c.media.filter((m) => m.id !== id) })); toast.success("Removed"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not remove"); }
  };

  if (!cfg) return <div className="min-h-screen bg-metros-black flex items-center justify-center"><Loader2 className="animate-spin text-metros-red" /></div>;
  const displayUrl = `${window.location.origin}/display?code=${cfg.code}`;

  return (
    <div className="min-h-screen bg-[#F9F9F9]" data-testid="staff-display-page">
      <header className="bg-metros-black text-white px-4 py-4 flex items-center justify-between">
        <button data-testid="display-back-btn" onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-white/70 hover:text-white"><ArrowLeft size={16} /> Back</button>
        <h1 className="font-heading font-extrabold text-lg flex items-center gap-2"><Monitor size={18} /> Customer Display</h1>
        <span className="w-14" />
      </header>

      <div className="max-w-2xl mx-auto p-4 space-y-4">
        <section className="bg-white border border-gray-100 rounded-2xl p-5">
          <h2 className="font-heading font-bold text-base md:text-lg">Display URL</h2>
          <p className="text-xs text-muted-foreground mt-1">Open this on the second tablet. Branch code: <b data-testid="display-code" className="text-metros-black">{cfg.code}</b></p>
          <div className="flex gap-2 mt-3">
            <input readOnly data-testid="display-url" value={displayUrl} className="flex-1 bg-gray-50 border border-gray-200 rounded-xl px-3 py-2.5 text-sm" />
            <button data-testid="copy-display-url" onClick={() => { navigator.clipboard?.writeText(displayUrl); toast.success("Copied"); }} className="bg-metros-black text-white rounded-xl px-4 text-sm font-bold flex items-center gap-1"><Copy size={14} /> Copy</button>
            <a data-testid="open-display" href={displayUrl} target="_blank" rel="noreferrer" className="border border-gray-200 rounded-xl px-3 flex items-center"><ExternalLink size={16} /></a>
          </div>
        </section>

        <section className="bg-white border border-gray-100 rounded-2xl p-5 space-y-3">
          <h2 className="font-heading font-bold text-base md:text-lg">Display text</h2>
          <textarea data-testid="display-text-input" value={text} onChange={(e) => setText(e.target.value)} rows={2} maxLength={500} placeholder="e.g. Ask about our loyalty rewards!" className="w-full bg-white border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-metros-red" />
          <div className="flex items-center justify-between gap-3">
            <label className="flex items-center gap-2 text-sm font-semibold">
              <input data-testid="display-text-toggle" type="checkbox" checked={!!cfg.text_visible} onChange={(e) => save({ text_visible: e.target.checked })} className="accent-metros-red w-4 h-4" /> Show text on display
            </label>
            <button data-testid="save-display-text" disabled={saving} onClick={() => save({ text })} className="bg-metros-red text-white rounded-full px-5 py-2 text-sm font-bold disabled:opacity-50">Save text</button>
          </div>
        </section>

        <section className="bg-white border border-gray-100 rounded-2xl p-5">
          <h2 className="font-heading font-bold text-base md:text-lg">Customer card duration</h2>
          <p className="text-xs text-muted-foreground mt-1">Seconds the customer's info stays on screen after points are added or redeemed, before returning to ads.</p>
          <div className="flex gap-2 mt-3">
            <input data-testid="display-seconds-input" type="number" min="3" max="120" value={seconds} onChange={(e) => setSeconds(e.target.value)} className="w-28 bg-white border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-metros-red" />
            <button data-testid="save-display-seconds" disabled={saving} onClick={() => save({ customer_seconds: parseInt(seconds, 10) || 10 })} className="bg-metros-black text-white rounded-xl px-5 text-sm font-bold disabled:opacity-50">Save</button>
          </div>
        </section>

        <section className="bg-white border border-gray-100 rounded-2xl p-5">
          <h2 className="font-heading font-bold text-base md:text-lg">Staff unlock PIN</h2>
          <p className="text-xs text-muted-foreground mt-1">Asked after tapping the top-right corner of the display 5 times. 4–6 digits; leave blank for no PIN.</p>
          <div className="flex gap-2 mt-3">
            <input data-testid="display-pin-input" type="text" inputMode="numeric" pattern="[0-9]*" maxLength={6} value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))} placeholder="e.g. 2468" className="w-40 bg-white border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-metros-red tracking-widest" />
            <button data-testid="save-display-pin" disabled={saving || (pin && pin.length < 4)} onClick={() => save({ unlock_pin: pin })} className="bg-metros-black text-white rounded-xl px-5 text-sm font-bold disabled:opacity-50">Save</button>
          </div>
        </section>

        <section className="bg-white border border-gray-100 rounded-2xl p-5">
          <h2 className="font-heading font-bold text-base md:text-lg">Thank You screen duration</h2>
          <p className="text-xs text-muted-foreground mt-1">Seconds the Thank You screen (with rewards QR) stays before ads resume.</p>
          <div className="flex gap-2 mt-3">
            <input data-testid="display-thanks-seconds-input" type="number" min="2" max="120" value={thankSeconds} onChange={(e) => setThankSeconds(e.target.value)} className="w-28 bg-white border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-metros-red" />
            <button data-testid="save-display-thanks-seconds" disabled={saving} onClick={() => save({ thank_you_seconds: parseInt(thankSeconds, 10) || 8 })} className="bg-metros-black text-white rounded-xl px-5 text-sm font-bold disabled:opacity-50">Save</button>
          </div>
        </section>

        <section className="bg-white border border-gray-100 rounded-2xl p-5">
          <h2 className="font-heading font-bold text-base md:text-lg">Image duration</h2>
          <p className="text-xs text-muted-foreground mt-1">Seconds each image is shown before moving to the next ad.</p>
          <div className="flex gap-2 mt-3">
            <input data-testid="display-image-seconds-input" type="number" min="2" max="60" value={imageSeconds} onChange={(e) => setImageSeconds(e.target.value)} className="w-28 bg-white border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-metros-red" />
            <button data-testid="save-display-image-seconds" disabled={saving} onClick={() => save({ image_seconds: parseInt(imageSeconds, 10) || 8 })} className="bg-metros-black text-white rounded-xl px-5 text-sm font-bold disabled:opacity-50">Save</button>
          </div>
        </section>

        <section className="bg-white border border-gray-100 rounded-2xl p-5">
          <h2 className="font-heading font-bold text-base md:text-lg">Video repeat</h2>
          <div className="flex flex-col sm:flex-row gap-3 mt-3">
            {[["all", "Repeat all videos in a loop"], ["single", "Repeat single video"]].map(([v, label]) => (
              <label key={v} className={`flex-1 flex items-center gap-2 border rounded-xl px-4 py-3 text-sm font-semibold cursor-pointer ${(cfg.video_mode || "all") === v ? "border-metros-red bg-metros-red/5" : "border-gray-200"}`}>
                <input data-testid={`video-mode-${v}`} type="radio" name="video_mode" checked={(cfg.video_mode || "all") === v} onChange={() => save({ video_mode: v })} className="accent-metros-red" /> {label}
              </label>
            ))}
          </div>
        </section>

        <section className="bg-white border border-gray-100 rounded-2xl p-5">
          <div className="flex items-center justify-between">
            <h2 className="font-heading font-bold text-base md:text-lg">Ads (images & videos)</h2>
            <input ref={fileRef} type="file" accept="image/*,video/*" onChange={onFile} className="hidden" data-testid="display-file-input" />
            <button data-testid="upload-media-btn" disabled={progress !== null} onClick={() => fileRef.current?.click()} className="bg-metros-red text-white rounded-full px-5 py-2 text-sm font-bold disabled:opacity-50">
              {progress !== null ? `Uploading ${progress}%` : "Upload"}
            </button>
          </div>
          {(cfg.media || []).length === 0 && <p className="text-sm text-muted-foreground mt-3">No ads yet. Upload images or videos to loop on the display.</p>}
          {(cfg.media || []).length > 1 && <p className="text-xs text-muted-foreground mt-3">Drag ads to change the order they play in.</p>}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-4">
            {(cfg.media || []).map((m, i) => (
              <div key={m.id} data-testid={`display-media-${m.id}`} draggable
                onDragStart={() => { dragId.current = m.id; }}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { e.preventDefault(); const from = cfg.media.findIndex((x) => x.id === dragId.current); dragId.current = null; move(from, i); }}
                className="relative rounded-xl overflow-hidden border border-gray-100 bg-gray-50 aspect-video cursor-grab active:cursor-grabbing">
                {m.resource_type === "video" ? <video src={m.url} muted className="w-full h-full object-cover pointer-events-none" /> : <img src={m.url} alt="" draggable={false} className="w-full h-full object-cover pointer-events-none" />}
                <span className="absolute top-1.5 left-1.5 bg-black/60 text-white rounded-md px-1.5 py-0.5 text-[11px] font-bold flex items-center gap-1">{i + 1} {m.resource_type === "video" ? <Video size={12} /> : <ImageIcon size={12} />}</span>
                <button data-testid={`delete-media-${m.id}`} onClick={() => remove(m.id)} className="absolute top-1.5 right-1.5 bg-white/90 text-metros-red rounded-md p-1"><Trash2 size={14} /></button>
                <div className="absolute bottom-1.5 right-1.5 flex gap-1">
                  <button data-testid={`media-up-${m.id}`} disabled={i === 0} onClick={() => move(i, i - 1)} className="bg-white/90 rounded-md p-1 disabled:opacity-30"><ChevronLeft size={14} /></button>
                  <button data-testid={`media-down-${m.id}`} disabled={i === cfg.media.length - 1} onClick={() => move(i, i + 1)} className="bg-white/90 rounded-md p-1 disabled:opacity-30"><ChevronRight size={14} /></button>
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
